import { esc } from "../ui/format.js";
import { ui3DBars, uiCard, uiEmpty, uiGet, uiHeader, uiKpis, uiMoney, uiTabs, uiTone, uiTrendChart, uiWireTabs } from "../ui/kit.js";

/**
 * World Ranking — where your company stands among the world's companies.
 *
 * Rank medallion (world / sector / country / industry league), a 3D
 * skyline of you against the companies just above, the industry league
 * against your rivals (the race that moves month to month), who's next to
 * overtake and how long it would take, milestones, revenue history, and the
 * leaderboard: world top 100, a table for every industry, and your country,
 * all real companies (backend/real_companies.py). Data: /api/global-ranking.
 */

let rkScope = "world";
let rkMetric = "revenue";
let rkView = "companies"; // "companies" | "people"
let rkSector = null;

function rkEta(m) {
  if (m === 0) return "passed";
  if (m === null || m === undefined) return "not at your current growth";
  if (m < 24) return `~${m} months`;
  return `~${Math.round(m / 12)} years`;
}

function rkMedal(r) {
  return `
    <div class="rk-medal">
      <div class="rk-medal-ring"><div><span>#</span>${r.world_rank}</div></div>
      <div class="rk-medal-text">
        <div class="sx-eyebrow">World rank by revenue</div>
        <div class="rk-medal-of">of ${r.world_total} companies</div>
        <div class="rk-medal-sub">${r.in_top_100 ? `<span class="pos">Inside the Global Top 100.</span>`
          : `${uiMoney(r.gap_to_top100, { compact: true })} of annual revenue short of the Global Top 100.`}</div>
      </div>
    </div>`;
}

function rkLeague(r) {
  const top = r.industry_league.slice(0, 8);
  const max = Math.max(...top.map((x) => x.annual_revenue), 1);
  return `
    <div class="rk-league">${top.map((x) => `
      <div class="rk-league-row ${x.you ? "you" : ""}">
        <span class="rk-pos">${x.position}</span>
        <span class="rk-name">${esc(x.name)}${x.you ? " (you)" : ""}<em>${esc(x.strategy || "")}</em></span>
        <div class="rk-bar"><i style="width:${Math.max(2, (x.annual_revenue / max) * 100).toFixed(1)}%"></i></div>
        <b>${uiMoney(x.annual_revenue, { compact: true })}</b>
      </div>`).join("")}
      ${r.league_rank > 8 ? `<div class="rk-league-row you"><span class="rk-pos">${r.league_rank}</span><span class="rk-name">${esc(r.company)} (you)</span><div></div><b>${uiMoney(r.annual_revenue, { compact: true })}</b></div>` : ""}
    </div>`;
}

// Rachit (2026-10-22): rank by revenue, workforce, valuation or profit, not only revenue.
const RK_VALUE = {
  annual_revenue: (x) => uiMoney(x.annual_revenue, { compact: true }),
  valuation: (x) => `${uiMoney(x.valuation, { compact: true })}${x.valuation_basis === "est" ? ' <span class="rk-yr" title="No market value listed: 15x profit">est.</span>' : x.valuation_basis === "approx" ? ' <span class="rk-yr" title="Approximate market value, late 2025">≈</span>' : ""}`,
  profit: (x) => uiMoney(x.profit, { compact: true }),
  employees: (x) => (x.employees || 0).toLocaleString("en-US"),
};

function rkBoard(lb) {
  let last = 0;
  const key = lb.metric_key || "annual_revenue";
  return `<table class="sx-table rk-table"><thead><tr><th>#</th><th>Company</th><th>Sector</th><th>HQ</th><th class="num" title="${esc(lb.metric_what || "")}">${esc(lb.metric_label || "Revenue")}${key === "annual_revenue" ? " / yr" : ""}</th></tr></thead><tbody>
    ${lb.rows.map((x) => {
      const gap = x.position - last > 1 ? `<tr class="rk-gap"><td colspan="5">⋯</td></tr>` : "";
      last = x.position;
      return `${gap}<tr class="${x.you ? "rk-you" : ""}"><td class="mono">${x.position}</td><td>${esc(x.name)}${x.you ? " <b>(you)</b>" : ""}${x.year ? ` <span class="rk-yr">${x.year}</span>` : ""}</td>
        <td>${esc(x.sector)}</td><td>${esc(x.country)}</td><td class="num">${RK_VALUE[key](x)}</td></tr>`;
    }).join("")}</tbody></table>`;
}

const RK_SWITCH = () => `<nav class="rk-switch" role="tablist"><button type="button" data-rk-view="companies" class="${rkView === "companies" ? "on" : ""}">🏢 Companies</button><button type="button" data-rk-view="people" class="${rkView === "people" ? "on" : ""}">👤 Richest people</button></nav>`;

async function renderRanking(main, snap) {
  if (rkView === "people") {
    main.innerHTML = `<div class="sx rk">${uiHeader("World Ranking", "You, the founder, among the world's richest people, by what your stake in the company is worth.")}${RK_SWITCH()}<div id="rk-people"><div class="sx-forecast-loading">Loading the world's richest people…</div></div></div>`;
    main.querySelectorAll("[data-rk-view]").forEach((b) => b.addEventListener("click", () => { rkView = b.dataset.rkView; renderRanking(main, snap); }));
    await window.renderPeopleRankingInto(main.querySelector("#rk-people"));
    return;
  }
  const q = new URLSearchParams({ scope: rkScope, metric: rkMetric });
  if (rkScope === "sector" && rkSector) q.set("sector", rkSector);
  const r = await uiGet(`/global-ranking?${q}`);
  const pick = r.leaderboard.sector || r.sector;
  const skyline = [...r.below.slice().reverse().map((x) => ({ label: x.name, value: x.annual_revenue, sub: `#${x.world_rank}` })),
    { label: "You", value: Math.max(1, r.annual_revenue), sub: `#${r.world_rank}`, highlight: true },
    ...r.above.slice().reverse().map((x) => ({ label: x.name, value: x.annual_revenue, sub: `#${x.world_rank}` })),
    { label: "#100 cut-off", value: r.top100_floor, sub: "Global Top 100", tone: "muted" },
    { label: r.number_one.name, value: r.number_one.annual_revenue, sub: "#1", tone: "muted" }];
  const leagueBars = r.industry_league.slice(0, 6).map((x) => ({ label: x.you ? "You" : x.name, value: Math.max(1, x.annual_revenue),
                                                               sub: `#${x.position}`, highlight: x.you }));
  main.innerHTML = `
    <div class="sx rk">
      ${uiHeader("World Ranking", "Where your company stands among the world's largest companies, ranked by annual revenue the way global league tables are.")}
      ${RK_SWITCH()}
      <div class="rk-hero">
        ${rkMedal(r)}
        ${uiKpis([
          { label: `Sector rank · ${r.sector}`, value: `#${r.sector_rank} <small>of ${r.sector_total}</small>` },
          { label: `Country rank · ${r.country}`, value: `#${r.country_rank} <small>of ${r.country_total}</small>` },
          { label: "Industry league", value: `#${r.league_rank} <small>of ${r.league_total}</small>`, sub: "you vs your rivals" },
          { label: "Annual revenue", value: uiMoney(r.annual_revenue, { compact: true }),
            sub: `${r.monthly_growth_pct >= 0 ? "+" : ""}${r.monthly_growth_pct}%/mo recently`, subTone: uiTone(r.monthly_growth_pct) },
        ])}
      </div>
      <div class="rk-vs">ℹ Ranked by <b>yearly revenue</b> (${uiMoney(r.annual_revenue, { compact: true })}), like the Fortune Global 500. Your <b>valuation</b>
        (${uiMoney((window.__snapshot && window.__snapshot.ratios && window.__snapshot.ratios.valuation) || 0, { compact: true })}) is that revenue times your industry's multiple.
        <button type="button" class="rk-vs-btn" data-open-valuation>How valuation is counted</button></div>
      <div class="sx-grid-2">
        ${uiCard("The skyline", ui3DBars(skyline, { height: 230, log: true }), { sub: "You, the companies just above, the Top-100 cut-off and #1 (log scale: heights compare orders of magnitude)" })}
        ${uiCard("Next to overtake", r.above.length ? `<div class="rk-next">${r.above.slice().reverse().slice(0, 4).map((x) => `
            <div class="rk-next-card"><div class="sx-eyebrow">World #${x.world_rank} · ${esc(x.sector)}</div>
              <b>${esc(x.name)}</b>
              <div class="rk-next-line"><span>Revenue gap</span><b>${uiMoney(x.gap, { compact: true })}</b></div>
              <div class="rk-next-line"><span>At your growth</span><b>${rkEta(x.months_to_overtake)}</b></div></div>`).join("")}</div>`
          : uiEmpty("You're #1", "Nobody in the world out-earns you."), { sub: "The next names above you in the world table" })}
      </div>
      <div class="sx-grid-2">
        ${uiCard("Industry league", `${ui3DBars(leagueBars, { height: 190 })}${rkLeague(r)}`, { sub: "The race against your rivals: it moves every month" })}
        ${uiCard("Milestones", `<ol class="rk-miles">${r.milestones.map((m) => `
          <li class="${m.done ? "done" : ""}"><i></i><div><b>${esc(m.label)}</b><span>${uiMoney(m.revenue, { compact: true })} / yr · ${m.done ? "achieved" : rkEta(m.months)}</span></div></li>`).join("")}</ol>`,
          { sub: "The road from where you are to #1" })}
      </div>
      ${r.history.length >= 2 ? uiCard("Your annual revenue, month by month", uiTrendChart(r.history.map((h) => h.annual_revenue), { labels: r.history.map((h) => `M${h.month}`) }),
        { sub: `World rank then: #${r.history[0].world_rank} → now #${r.world_rank}` }) : ""}
      ${uiCard(rkScope === "world" ? "World's largest companies" : rkScope === "sector" ? `Largest companies in ${pick}` : `Largest companies in ${r.country}`,
        `${uiTabs([["world", "World Top 100"], ["sector", "By industry"], ["country", "Your country"]], rkScope, "rk-scope")}
        ${rkScope === "sector" ? `<div class="rk-sectors">${r.sectors.map((sec) => `<button type="button" class="rk-sector ${sec === pick ? "on" : ""}" data-rk-sector="${esc(sec)}">${esc(sec)}${sec === r.sector ? " · yours" : ""}</button>`).join("")}</div>` : ""}
        <div class="mv-chips rk-metrics" role="group" aria-label="Rank by">${(r.leaderboard.metrics || []).map((m) => `<button type="button" class="mv-chip${m.key === rkMetric ? " on" : ""}" data-rk-metric="${m.key}" title="${esc(m.what)}">By ${esc(m.label.toLowerCase())}</button>`).join("")}</div>
        <div class="rk-board">${rkBoard(r.leaderboard)}</div>`,
        { sub: `You are #${r.leaderboard.your_position} of ${r.leaderboard.total}` })}
      <p class="lx-note">${esc(r.method)}</p>
      <p class="lx-note rk-source">${r.data.real ? "Real-world data" : "Fallback data"}: ${esc(r.data.source || "")}${r.data.fetched_at ? ` Last updated ${esc(r.data.fetched_at.slice(0, 10))}${r.data.fx_date ? `, exchange rates of ${esc(r.data.fx_date)}` : ""}.` : ""}${r.data.refreshing ? " Refreshing now…" : r.data.real ? " Updates daily while the game is running." : ""}</p>
    </div>`;
  main.querySelectorAll("[data-rk-view]").forEach((b) => b.addEventListener("click", () => { rkView = b.dataset.rkView; renderRanking(main, snap); }));
  main.querySelectorAll("[data-open-valuation]").forEach((b) => b.addEventListener("click", () => window.openValuationExplainer()));
  uiWireTabs(main, "rk-scope", (key) => { rkScope = key; renderRanking(main, snap); });
  main.querySelectorAll("[data-rk-sector]").forEach((b) => b.addEventListener("click", () => { rkSector = b.dataset.rkSector; renderRanking(main, snap); }));
  main.querySelectorAll("[data-rk-metric]").forEach((b) => b.addEventListener("click", () => { rkMetric = b.dataset.rkMetric; renderRanking(main, snap); }));
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderRanking = renderRanking;
